import React, { useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import ROUTES from '../../consts/paths'
import Cookies from 'js-cookie'
import { useAuthenticatedContext } from '../../hooks/AuthenticatedContext'
// • Route guard for authenticated pages (renders children via <Outlet /> when session looks valid)
// • Validates required login context (authType + tenantKey) and forces logout when session is missing/invalid
// • Supports both auth modes: Edlink (cookie-based) and Cognito (Amplify authStatus-based)
export const PrivateRoute: React.FC = (): JSX.Element => {
  const { isAuthed, cognitoIdpContext } = useAuthenticatedContext()
  const navigate = useNavigate()

  // • Edlink session validity is inferred from existence of access/refresh token cookies
  const isValidEdlink = Cookies.get('edlink_access_token_exists') || Cookies.get('edlink_refresh_token_exists')

  // • Initial gate: if app is missing essential auth context, force a clean logout
  // • Prevents users from landing on private pages with incomplete localStorage state
  useEffect(() => {
    if (!localStorage.getItem('authType') || !localStorage.getItem('tenantKey')) {
      navigate(ROUTES.LOGOUT)
    }
  }, [])

  // • Edlink guard:
  // • If authType is edlink but user isn’t authed AND no valid Edlink cookies exist → logout
  useEffect(() => {
    if (!isAuthed && !isValidEdlink && localStorage.getItem('authType') === 'edlink') {
      navigate(ROUTES.LOGOUT, { replace: true })
    }
  }, [isAuthed, isValidEdlink, navigate])

  // • Cognito guard:
  // • If authType is cognito but user isn’t authed AND Amplify reports unauthenticated/signOut → logout
  // • Uses cognitoIdpContext.authStatus as the source of truth for Cognito session state
  useEffect(() => {
    if (!isAuthed && (cognitoIdpContext.authStatus === 'unauthenticated' || cognitoIdpContext.authStatus === 'signOut') && localStorage.getItem('authType') === 'cognito') {
      navigate(ROUTES.LOGOUT, { replace: true })
    }
  }, [isAuthed, isValidEdlink, navigate])

  return (<Outlet />)
}
